import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { CheckCircle, XCircle, Eye, Clock, AlertTriangle, MessageCircle } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';

interface CourseProposal {
  id: string;
  title: string;
  description: string;
  status: string;
  duration_hours: number;
  max_participants: number;
  is_online: boolean;
  location?: string;
  budget_estimate?: number;
  created_at: string;
  proposed_by: string;
  reviewer_id?: string;
  review_notes?: string;
  subject_area?: string;
  proficiency_level?: string;
  profiles?: {
    first_name: string;
    last_name: string;
    email: string;
  };
}

export default function ProposalQueue() {
  const { user } = useAuth();
  const [proposals, setProposals] = useState<CourseProposal[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedProposal, setSelectedProposal] = useState<CourseProposal | null>(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [reviewAction, setReviewAction] = useState<'approve' | 'reject' | 'revision'>('approve');

  useEffect(() => {
    fetchProposals();
    
    // Subscribe to real-time updates for proposals
    const channel = supabase
      .channel('proposal-queue-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'course_proposals'
        },
        (payload) => {
          console.log('Real-time proposal queue update:', payload);
          // Refresh proposals when any proposal changes
          fetchProposals();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const fetchProposals = async () => {
    try {
      console.log('Fetching course proposals...');
      const { data, error } = await supabase
        .from('course_proposals')
        .select(`
          *,
          profiles!fk_course_proposals_proposed_by(first_name, last_name, email)
        `)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching proposals:', error);
        throw error;
      }
      
      console.log('Raw fetched proposals:', data);
      
      // Transform the data to match expected format
      const transformedData = data?.map(proposal => {
        console.log(`Proposal ${proposal.title}: status=${proposal.status}, review_notes=${proposal.review_notes}`);
        return {
          ...proposal,
          proposer: proposal.profiles ? {
            first_name: proposal.profiles.first_name,
            last_name: proposal.profiles.last_name,
            email: proposal.profiles.email
          } : null
        };
      });
      
      console.log('Transformed proposals for state:', transformedData);
      setProposals(transformedData || []);
    } catch (error) {
      console.error('Error fetching proposals:', error);
      toast.error('Failed to load course proposals');
    } finally {
      setLoading(false);
    }
  };

  const handleReview = async () => {
    if (!selectedProposal) {
      console.error('No proposal selected for review');
      return;
    }

    console.log('Starting review process...', {
      proposalId: selectedProposal.id,
      reviewAction,
      reviewNotes,
      userId: user?.id
    });

    try {
      // First, check if user is admin
      const { data: userProfile, error: profileError } = await supabase
        .from('profiles')
        .select('is_admin, first_name, last_name')
        .eq('id', user?.id)
        .single();
      
      if (profileError) {
        console.error('Error checking user profile:', profileError);
        throw new Error('Could not verify admin permissions');
      }
      
      console.log('User profile check:', userProfile);
      
      if (!userProfile?.is_admin) {
        throw new Error('User does not have admin permissions to review proposals');
      }

      let newStatus = reviewAction === 'approve' ? 'approved' : 
                     reviewAction === 'reject' ? 'rejected' : 'needs_revision';

      console.log('Updating proposal with admin user:', {
        id: selectedProposal.id,
        status: newStatus,
        reviewer_id: user?.id,
        review_notes: reviewNotes,
        reviewed_at: new Date().toISOString(),
        adminStatus: userProfile.is_admin
      });

      const { data, error } = await supabase
        .from('course_proposals')
        .update({
          status: newStatus,
          reviewer_id: user?.id,
          review_notes: reviewNotes,
          reviewed_at: new Date().toISOString()
        })
        .eq('id', selectedProposal.id)
        .select(); // Add select to see what was updated

      if (error) {
        console.error('Supabase error during update:', error);
        throw error;
      }

      console.log('Proposal update result:', data);

      // Verify the update worked
      const { data: verifyData, error: verifyError } = await supabase
        .from('course_proposals')
        .select('id, status, review_notes, reviewer_id')
        .eq('id', selectedProposal.id)
        .single();
      
      console.log('Verification query result:', verifyData, verifyError);

      if (verifyData?.status !== newStatus) {
        throw new Error(`Update failed: Expected status '${newStatus}' but got '${verifyData?.status}'`);
      }

      // Send notification to the proposer (non-blocking)  
      try {
        await sendNotificationToProposer(selectedProposal, newStatus, reviewNotes);
      } catch (notificationError) {
        console.log('Notification failed but continuing with approval:', notificationError);
      }

      // If approved, create the actual course
      if (newStatus === 'approved') {
        console.log('Creating course from approved proposal...');
        await createCourseFromProposal(selectedProposal);
      }

      toast.success(`Proposal ${reviewAction}d successfully`);
      
      // Close dialog and reset state immediately
      setReviewDialogOpen(false);
      setSelectedProposal(null);
      setReviewNotes('');
      
      // Force refresh after a short delay to ensure database consistency
      setTimeout(() => {
        console.log('Forcing proposal refresh...');
        fetchProposals();
      }, 1000);
      
    } catch (error) {
      console.error('Error reviewing proposal:', error);
      toast.error(`Failed to review proposal: ${error.message}`);
    }
  };

  const createCourseFromProposal = async (proposal: CourseProposal) => {
    try {
      console.log('Creating course from proposal:', proposal.title);
      
      // First, clean up any existing draft courses with the same title
      console.log('Cleaning up existing draft courses with title:', proposal.title);
      const { error: deleteError } = await supabase
        .from('courses')
        .delete()
        .eq('title', proposal.title)
        .eq('status', 'draft')
        .eq('created_by', proposal.proposed_by);

      if (deleteError) {
        console.warn('Error cleaning up draft courses:', deleteError);
        // Don't throw error here, just log warning as this shouldn't block course creation
      } else {
        console.log('Successfully cleaned up draft courses');
      }
      
      const courseData = {
        title: proposal.title,
        description: proposal.description,
        duration_hours: proposal.duration_hours,
        max_participants: proposal.max_participants,
        is_online: proposal.is_online,
        location: proposal.location,
        status: 'published', // Publish immediately when proposal approved
        created_by: user?.id,
        // Use proposal fields that exist
        target_departments: (proposal as any).target_departments || [],
        target_job_types: (proposal as any).target_job_types || [],
        target_grade_levels: (proposal as any).target_grade_levels || []
      };

      const { data: course, error } = await supabase
        .from('courses')
        .insert(courseData)
        .select()
        .single();

      if (error) {
        console.error('Error creating course:', error);
        throw error;
      }

      console.log('Course created successfully:', course);
      toast.success(`Course "${proposal.title}" created successfully! You can find it in the course management section.`);
      
      // Update the proposal to link to the created course
      await supabase
        .from('course_proposals')  
        .update({ 
          review_notes: `${reviewNotes}\n\nCourse created successfully with ID: ${course.id}`
        })
        .eq('id', proposal.id);
        
    } catch (error) {
      console.error('Error creating course from proposal:', error);
      toast.error('Proposal approved but failed to create course. Please create the course manually.');
    }
  };

  const sendNotificationToProposer = async (proposal: CourseProposal, status: string, notes: string) => {
    try {
      console.log('Creating in-app notification for proposal:', proposal.id);
      
      // Create in-app notification only
      const notificationTitle = `Course Proposal ${status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}`;
      const notificationMessage = getNotificationMessage(status, proposal.title, notes);
      
      const { error: notificationError } = await supabase
        .from('notifications')
        .insert({
          user_id: proposal.proposed_by,
          title: notificationTitle,
          message: notificationMessage,
          type: status === 'approved' ? 'success' : status === 'rejected' ? 'error' : 'warning',
          related_id: proposal.id,
          related_type: 'proposal'
        });

      if (notificationError) {
        console.error('Error creating notification:', notificationError);
        throw notificationError;
      } else {
        console.log('In-app notification created successfully');
      }
      
    } catch (error) {
      console.error('Error sending notification:', error);
      // Don't throw error - just log it so approval can continue
    }
  };

  const getNotificationMessage = (status: string, title: string, notes: string) => {
    const statusMessages = {
      approved: `Great news! Your course proposal "${title}" has been approved.`,
      rejected: `Your course proposal "${title}" was not approved at this time.`,
      needs_revision: `Your course proposal "${title}" needs some revisions before it can be approved.`
    };
    
    return `${statusMessages[status as keyof typeof statusMessages]} ${notes ? `\n\nReviewer notes: ${notes}` : ''}`;
  };

  const openReviewDialog = (proposal: CourseProposal, action: 'approve' | 'reject' | 'revision') => {
    setSelectedProposal(proposal);
    setReviewAction(action);
    setReviewDialogOpen(true);
  };

  const getStatusBadge = (status: string) => {
    const variants = {
      pending: { variant: 'secondary' as const, icon: Clock, color: 'text-yellow-600' },
      under_review: { variant: 'secondary' as const, icon: Eye, color: 'text-blue-600' },
      approved: { variant: 'default' as const, icon: CheckCircle, color: 'text-green-600' },
      rejected: { variant: 'destructive' as const, icon: XCircle, color: 'text-red-600' },
      needs_revision: { variant: 'secondary' as const, icon: AlertTriangle, color: 'text-orange-600' }
    };

    const config = variants[status as keyof typeof variants];
    const Icon = config.icon;
    
    return (
      <Badge variant={config.variant} className="gap-1">
        <Icon className={`h-3 w-3 ${config.color}`} />
        {status.replace('_', ' ').toUpperCase()}
      </Badge>
    );
  };

  const pendingProposals = proposals.filter(p => p.status === 'pending');
  const reviewedProposals = proposals.filter(p => p.status !== 'pending');

  if (loading) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center">Loading proposals...</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-foreground">Proposal Review Queue</h1>
        <p className="text-muted-foreground">Review and approve course proposals from district staff</p>
      </div>

      <Tabs defaultValue="pending" className="space-y-4">
        <TabsList>
          <TabsTrigger value="pending">
            Pending Review ({pendingProposals.length})
          </TabsTrigger>
          <TabsTrigger value="reviewed">
            Reviewed ({reviewedProposals.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="space-y-4">
          {pendingProposals.length === 0 ? (
            <Card>
              <CardContent className="pt-6">
                <div className="text-center text-muted-foreground">
                  No proposals pending review
                </div>
              </CardContent>
            </Card>
          ) : (
            pendingProposals.map((proposal) => (
              <Card key={proposal.id} className="border-l-4 border-l-yellow-500">
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div className="space-y-2">
                      <CardTitle>{proposal.title}</CardTitle>
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <span>Proposed by: {proposal.profiles?.first_name} {proposal.profiles?.last_name}</span>
                        <span>•</span>
                        <span>{new Date(proposal.created_at).toLocaleDateString()}</span>
                        <span>•</span>
                        <span>{proposal.duration_hours}h duration</span>
                      </div>
                    </div>
                    {getStatusBadge(proposal.status)}
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground mb-4">{proposal.description}</p>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 text-sm">
                    <div>
                      <span className="font-medium">Max Participants:</span>
                      <p>{proposal.max_participants || 'Not specified'}</p>
                    </div>
                    <div>
                      <span className="font-medium">Format:</span>
                      <p>{proposal.is_online ? 'Online' : 'In-Person'}</p>
                    </div>
                    <div>
                      <span className="font-medium">Location:</span>
                      <p>{proposal.location || 'Not specified'}</p>
                    </div>
                    <div>
                      <span className="font-medium">Budget:</span>
                      <p>{proposal.budget_estimate ? `$${proposal.budget_estimate}` : 'Not specified'}</p>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => openReviewDialog(proposal, 'approve')}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openReviewDialog(proposal, 'revision')}
                    >
                      <MessageCircle className="h-4 w-4 mr-2" />
                      Request Revision
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => openReviewDialog(proposal, 'reject')}
                    >
                      <XCircle className="h-4 w-4 mr-2" />
                      Reject
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>

        <TabsContent value="reviewed" className="space-y-4">
          {reviewedProposals.map((proposal) => (
            <Card key={proposal.id}>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div className="space-y-2">
                    <CardTitle>{proposal.title}</CardTitle>
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <span>Proposed by: {proposal.profiles?.first_name} {proposal.profiles?.last_name}</span>
                      <span>•</span>
                      <span>{new Date(proposal.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                  {getStatusBadge(proposal.status)}
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground mb-2">{proposal.description}</p>
                {proposal.review_notes && (
                  <div className="bg-muted p-3 rounded-lg mt-4">
                    <p className="font-medium text-sm mb-1">Review Notes:</p>
                    <p className="text-sm text-muted-foreground">{proposal.review_notes}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </TabsContent>
      </Tabs>

      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {reviewAction === 'approve' && 'Approve Proposal'}
              {reviewAction === 'reject' && 'Reject Proposal'}
              {reviewAction === 'revision' && 'Request Revision'}
            </DialogTitle>
            <DialogDescription>
              {selectedProposal?.title}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">
                {reviewAction === 'approve' && 'Approval Notes (Optional)'}
                {reviewAction === 'reject' && 'Reason for Rejection'}
                {reviewAction === 'revision' && 'Revision Requirements'}
              </label>
              <Textarea
                value={reviewNotes}
                onChange={(e) => setReviewNotes(e.target.value)}
                placeholder={
                  reviewAction === 'approve' ? 'Add any approval notes...' :
                  reviewAction === 'reject' ? 'Explain why this proposal was rejected...' :
                  'Describe what changes are needed...'
                }
                required={reviewAction !== 'approve'}
                rows={4}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleReview}
              disabled={reviewAction !== 'approve' && !reviewNotes.trim()}
              variant={reviewAction === 'reject' ? 'destructive' : 'default'}
            >
              {reviewAction === 'approve' && 'Approve Proposal'}
              {reviewAction === 'reject' && 'Reject Proposal'}
              {reviewAction === 'revision' && 'Request Revision'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}